/**
 * HTTP Interceptor con manejo de autenticación
 */

const normalizeApiBase = (baseUrl) => {
  const trimmed = (baseUrl || '').trim();
  if (!trimmed) {
    return 'http://localhost:8085/api/v1';
  }
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
};

/**
 * Fetch mejorado con manejo automático de autenticación
 */
export const fetchWithAuth = async (url, options = {}) => {
  const apiUrl = normalizeApiBase(process.env.REACT_APP_LOAD_PAGE_URL);
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  const fullUrl = url.startsWith('http') ? url : `${apiUrl}${normalizedPath}`;
  
  // Obtener token del localStorage
  let token = localStorage.getItem('authToken');
  
  // Agregar headers de autenticación si no están presentes
  const headers = {
    'Cache-Control': 'no-store',
    'Pragma': 'no-cache',
    'Expires': '0',
    ...options.headers,
  };
  
  // Solo agregar Authorization si hay token y no es el endpoint de login inicial
  if (token && url !== '/login') {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Agregar Content-Type si no está presente y hay body
  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }
  
  const config = {
    ...options,
    cache: 'no-store',
    headers,
  };

  try {
    let response = await fetch(fullUrl, config);

      // Regla global única para toda la aplicación:
      // cualquier 401/403 fuerza re-login.
      if ((response.status === 401 || response.status === 403) && url !== '/login') {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('displayName');

        const authError = new Error('UNAUTHORIZED');
        authError.isAuthError = true;
        authError.status = response.status;

        if (typeof window !== 'undefined' && window.location.pathname !== '/') {
          window.location.href = '/';
        }

        throw authError;
      }

      // Si la respuesta no es ok, lanzar error con información
    if (!response.ok) {
      let backendMessage = '';
      try {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const payload = await response.json();
          backendMessage = payload?.message || JSON.stringify(payload);
        } else {
          backendMessage = await response.text();
        }
      } catch (parseError) {
        backendMessage = '';
      }

      const errorMessage = backendMessage
        ? backendMessage
        : `HTTP error! Status: ${response.status}`;
      const error = new Error(errorMessage);
      error.response = response;
      error.status = response.status;
      throw error;
    }

    return response;
  } catch (error) {
    if (!error.response && !error.status) {
      error.isNetworkError = true;
      if (!error.message || error.message === 'Failed to fetch') {
        error.message = `Network error calling ${fullUrl}`;
      }
    }
    throw error;
  }
};

const parseResponseBody = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();
  if (!text) {
    return null;
  }
  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }
  return text;
};

/**
 * Helper para peticiones GET
 */
export const get = async (url, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    method: 'GET',
  });
  return parseResponseBody(response);
};

/**
 * Helper para peticiones POST
 */
export const post = async (url, body, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    method: 'POST',
    body: body instanceof FormData ? body : JSON.stringify(body),
  });
  return parseResponseBody(response);
};

/**
 * Helper para peticiones PUT
 */
export const put = async (url, body, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    method: 'PUT',
    body: body instanceof FormData ? body : JSON.stringify(body),
  });
  
  // Siempre intentar parsear JSON si hay contenido
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    const text = await response.text();
    if (text) {
      try {
        return JSON.parse(text);
      } catch (e) {
        return { status: true, message: 'Successfully updated' };
      }
    }
  }
  // Si no hay contenido, devolver un objeto de éxito por defecto
  return { status: true, message: 'Successfully updated' };
};

/**
 * Helper para peticiones DELETE
 */
export const del = async (url, options = {}) => {
  const response = await fetchWithAuth(url, {
    ...options,
    method: 'DELETE',
  });
  return parseResponseBody(response);
};

export default fetchWithAuth;

